import React, { useState } from 'react';
import { MapPin, CheckCircle2, Send } from 'lucide-react';
import GooglePlacesAddress, { type AddressData } from '../shared/GooglePlacesAddress';
import type { RoutyMissingAddressArtifact } from '../../business/routy/routyArtifacts';

interface MissingAddressCardProps {
  artifact: RoutyMissingAddressArtifact;
  disabled?: boolean;
  onSubmit: (artifact: RoutyMissingAddressArtifact, address: string) => void;
}

function buildFullAddress(data: AddressData): string {
  const cityState = [data.city, data.state].filter(Boolean).join(', ');
  return [data.address1, cityState, data.zipCode].filter(Boolean).join(', ').trim();
}

const MissingAddressCard: React.FC<MissingAddressCardProps> = ({ artifact, disabled, onSubmit }) => {
  const [addressInput, setAddressInput] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [verified, setVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const locked = disabled || submitted;

  const handleSelect = (data: AddressData) => {
    const full = buildFullAddress(data);
    setAddressInput(full || addressInput);
    setFullAddress(full);
    setVerified(!!data.coordinates);
  };

  const handleInput = (value: string) => {
    if (value === addressInput) return;
    setAddressInput(value);
    setFullAddress('');
    setVerified(false);
  };

  const handleSubmit = () => {
    const address = fullAddress || addressInput.trim();
    if (!address) return;
    setSubmitted(true);
    onSubmit(artifact, address);
  };

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-sm">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900 truncate">{artifact.subject}'s address</span>
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.context && <p className="text-xs text-ink-500">{artifact.context}</p>}
        <div className="relative">
          <GooglePlacesAddress
            value={addressInput}
            onChange={handleSelect}
            onInputChange={handleInput}
            placeholder="Search address..."
            searchType="all"
            enableValidation={false}
            disabled={locked}
            className="w-full rounded-lg border border-ink-200 bg-surface pl-8 pr-8 py-2 text-sm placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-60"
          />
          <MapPin
            className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
              verified ? 'text-emerald-500' : 'text-ink-400'
            }`}
          />
          {verified && (
            <CheckCircle2 className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-500" />
          )}
        </div>
      </div>

      <div className="px-3 pb-3">
        {submitted ? (
          <div className="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-emerald-600">
            <CheckCircle2 className="w-4 h-4" />
            Sent to Routy
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={locked || !addressInput.trim()}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Use this address
          </button>
        )}
      </div>
    </div>
  );
};

export default MissingAddressCard;
